import { useCallback, useEffect, useState } from 'react';

/**
 * The width of an element, kept up to date as it changes. Returns the ref to
 * give the element and its width, which is `fallback` until it is measured
 * and wherever nothing can be (a test, a browser with no ResizeObserver).
 */
export function useElementWidth<T extends Element>(fallback: number): [(node: T | null) => void, number] {
  const [node, setNode] = useState<T | null>(null);
  const [width, setWidth] = useState(fallback);

  const ref = useCallback((next: T | null) => setNode(next), []);

  useEffect(() => {
    if (!node) return;

    const measured = node.getBoundingClientRect().width;
    if (measured > 0) setWidth(measured);

    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver((entries) => {
      const next = entries[0]?.contentRect.width ?? 0;
      if (next > 0) setWidth(next);
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, [node]);

  return [ref, width];
}
